import React, { useState, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import {
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Button,
  Card,
  CardBody,
  CardHeader,
  Alert,
} from 'reactstrap';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { phase2ValidationSchema } from '../../../utils/yupValidators';
import {
  formatCNPJ,
  formatCEP,
  formatPhone,
  cleanDigits,
  BRAZILIAN_STATES,
  TAX_REGIMES,
  COMPANY_TYPES,
} from '../../../utils/brazilianFields';
import { companiesService } from '../../../services/companiesService';
import { cepService } from '../../../services/cepService';
import { cnaeService } from '../../../services/cnaeService';
import { ramoAtividadeService, RamoAtividadeDto } from '../../../services/ramoAtividadeService';

interface Phase2CompanyFormProps {
  onSubmit: (values: any) => void;
  onPrevious: () => void;
  initialValues?: any;
  isSubmitting: boolean;
  companyId?: string; // Company GUID for folder structure
  isFinalPhase?: boolean;
  backendValidationError?: string | null; // Backend validation error to display
}

const Phase2CompanyForm: React.FC<Phase2CompanyFormProps> = ({
  onSubmit,
  onPrevious,
  initialValues,
  isSubmitting,
  companyId,
  isFinalPhase = false,
  backendValidationError = null,
}) => {
  const { t} = useTranslation();
  const [cnaePrimarySearchTerm, setCnaePrimarySearchTerm] = useState('');
  const [cnaePrimaryResults, setCnaePrimaryResults] = useState<any[]>([]);
  const [loadingCnaePrimary, setLoadingCnaePrimary] = useState(false);
  const [cnaeSecondarySearchTerm, setCnaeSecondarySearchTerm] = useState('');
  const [cnaeSecondaryResults, setCnaeSecondaryResults] = useState<any[]>([]);
  const [loadingCnaeSecondary, setLoadingCnaeSecondary] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [validatingCnpj, setValidatingCnpj] = useState(false);
  const [showInvoicePassword, setShowInvoicePassword] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(initialValues?.logoUrl || null);
  const [logoBase64, setLogoBase64] = useState<string | null>(null); // Store logo base64 for upload on submit
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [ramosAtividade, setRamosAtividade] = useState<RamoAtividadeDto[]>([]);
  const [loadingRamosAtividade, setLoadingRamosAtividade] = useState(false);

  const formik = useFormik({
    enableReinitialize: true, // Allow form to reinitialize when initialValues change
    initialValues: initialValues || {
      companyNumber: null,
      name: '',
      cnpj: '',
      taxRegime: undefined, // Use undefined instead of empty string for number fields
      companyType: undefined,
      cnaePrincipal: '',
      cnaeSecondary: '',
      nire: '',
      simplesCode: '',
      professionalEntity: '',
      stateRegistration: '',
      municipalRegistration: '',
      email: '',
      phone: '',
      headquartersAddress: {
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: '',
        cep: '',
      },
      invoicePassword: '',
      invoiceEmissionPortalUrl: '', // NEW: Link to fiscal note emission portal
      // Phase 2 New Fields
      porte: '',
      capitalSocial: undefined,
      dataAbertura: '',
      anexo: '',
      segmento: '',
      ramoAtividadeId: '',
      observacao: '',
      // Logo fields
      logoStorageId: '',
      logoUrl: '',
      logoContentHash: '',
    },
    validationSchema: phase2ValidationSchema,
    onSubmit: (values) => {
      console.log('Phase 2 Form - Submitting', values);
      console.log('Phase 2 Form - Validation Errors:', formik.errors);
      console.log('Phase 2 Form - Logo Base64 present?', !!logoBase64, logoBase64 ? `(${logoBase64.substring(0, 50)}...)` : 'No logo');

      // Clean all fields before submission
      const cleanedValues = {
        ...values,
        cnpj: cleanDigits(values.cnpj),
        phone: values.phone ? cleanDigits(values.phone) : '',
        taxRegime: values.taxRegime ? parseInt(values.taxRegime as any, 10) : undefined,
        companyType: values.companyType ? parseInt(values.companyType as any, 10) : null,
        headquartersAddress: values.headquartersAddress
          ? {
              ...values.headquartersAddress,
              cep: values.headquartersAddress.cep
                ? cleanDigits(values.headquartersAddress.cep)
                : '',
            }
          : null,
        // Include logo base64 data if a new logo was selected
        logoBase64: logoBase64 || undefined,
      };
      console.log('Phase 2 Form - Submitting with logoBase64?', !!cleanedValues.logoBase64);
      onSubmit(cleanedValues);
    },
  });

  // Handle form submission with validation check
  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Trigger validation on all fields
    const errors = await formik.validateForm();
    formik.setTouched({
      companyNumber: true,
      name: true,
      cnpj: true,
      taxRegime: true,
      companyType: true,
      cnaePrincipal: true,
      cnaeSecondary: true,
      nire: true,
      simplesCode: true,
      professionalEntity: true,
      stateRegistration: true,
      municipalRegistration: true,
      email: true,
      phone: true,
      headquartersAddress: {
        street: true,
        number: true,
        neighborhood: true,
        city: true,
        state: true,
        cep: true,
        complement: true,
      },
      employeeRange: true,
      invoicePassword: true,
      invoiceEmissionPortalUrl: true,
      porte: true,
      capitalSocial: true,
      dataAbertura: true,
      anexo: true,
      segmento: true,
      ramoAtividadeId: true,
      observacao: true,
    });

    // Check if there are validation errors
    if (Object.keys(errors).length > 0) {
      toast.error(t('Por favor, preencha todos os campos obrigatórios corretamente.'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // If validation passes, submit the form
    formik.handleSubmit(e);
  };

  // Scroll to top when validation errors occur after submission
  useEffect(() => {
    if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
      toast.error(t('Por favor, preencha todos os campos obrigatórios corretamente.'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [formik.submitCount, formik.errors, t]);

  // Handle backend validation errors
  useEffect(() => {
    if (backendValidationError) {
      // Parse backend validation error message to extract field name
      // Format: "Validation failed: \r\n -- Segmento: Segmento é obrigatório. Severity: Error"
      const fieldNameMatch = backendValidationError.match(/--\s+(\w+):/);

      if (fieldNameMatch) {
        const fieldName = fieldNameMatch[1]; // e.g., "Segmento"
        const fieldNameLowerCase = fieldName.charAt(0).toLowerCase() + fieldName.slice(1); // "segmento"

        // Set the formik error for the field
        formik.setFieldError(fieldNameLowerCase, backendValidationError);
        formik.setFieldTouched(fieldNameLowerCase, true);

        // Scroll to the field
        setTimeout(() => {
          const element = document.getElementById(fieldNameLowerCase);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
          }
        }, 100);
      } else {
        // If we can't parse the field name, just scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [backendValidationError, formik]);

  // Fetch Ramos de Atividade on component mount
  useEffect(() => {
    const fetchRamosAtividade = async () => {
      setLoadingRamosAtividade(true);
      try {
        const data = await ramoAtividadeService.getAllActive();
        setRamosAtividade(data);
      } catch (error) {
        console.error('Error fetching Ramos de Atividade:', error);
      } finally {
        setLoadingRamosAtividade(false);
      }
    };

    fetchRamosAtividade();
  }, []);

  // Map naturezaJuridica to company type enum
  const mapNaturezaJuridicaToCompanyType = (naturezaJuridica: string | null | undefined): number | undefined => {
    if (!naturezaJuridica) return undefined;

    const nj = naturezaJuridica.toLowerCase();

    // MEI
    if (nj.includes('microempreendedor individual') || nj.includes('mei')) {
      return 5; // MEI
    }

    // Unipessoal LTDA
    if (nj.includes('unipessoal') || nj.includes('sociedade limitada unipessoal')) {
      return 1; // Unipessoal LTDA
    }

    // EIRELI
    if (nj.includes('eireli')) {
      return 4; // Eireli
    }

    // Sociedade LTDA
    if (nj.includes('sociedade empresária limitada') ||
        nj.includes('sociedade limitada') ||
        nj.includes('ltda')) {
      return 2; // Sociedade LTDA
    }

    // Individual
    if (nj.includes('empresário individual') || nj.includes('empresário')) {
      return 3; // Individual
    }

    return undefined;
  };

  // CNPJ Formatting and Validation
  const handleCnpjChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCNPJ(e.target.value);
    formik.setFieldValue('cnpj', formatted);

    // Validate CNPJ when complete
    if (cleanDigits(formatted).length === 14) {
      setValidatingCnpj(true);
      try {
        const result = await companiesService.validateCnpj(formatted);
        if (!result.isValid) {
          formik.setFieldError('cnpj', result.errorMessage || 'CNPJ inválido');
        } else {
          // If valid, lookup company data from Receita Federal
          console.log('🔍 Looking up CNPJ:', formatted);
          const lookupData = await companiesService.lookupCnpj(formatted);
          console.log('📦 CNPJ Lookup Response:', lookupData);

          if (lookupData) {
            // Auto-populate form fields from CNPJ lookup

            // Razão Social - always update from CNPJ lookup (official source)
            if (lookupData.razaoSocial) {
              formik.setFieldValue('name', lookupData.razaoSocial);
            }

            // Company Type (from naturezaJuridica)
            if (lookupData.naturezaJuridica && !formik.values.companyType) {
              const companyType = mapNaturezaJuridicaToCompanyType(lookupData.naturezaJuridica);
              if (companyType) {
                formik.setFieldValue('companyType', companyType);
              }
            }

            // Tax Regime (from opcaoPeloSimples - default to Simples Nacional for small companies)
            if (!formik.values.taxRegime) {
              // Check if company is eligible for or opted for Simples Nacional
              const isSimples =
                lookupData.opcaoPeloSimples === true ||
                lookupData.opcaoPeloMei === true ||
                lookupData.naturezaJuridica?.toLowerCase().includes('mei') ||
                lookupData.naturezaJuridica?.toLowerCase().includes('microempreendedor individual') ||
                lookupData.porte?.toUpperCase().includes('MICRO');

              if (isSimples) {
                formik.setFieldValue('taxRegime', 1); // Simples Nacional
              }
            }

            // CNAE Principal
            if (lookupData.atividadePrincipal?.codigo) {
              formik.setFieldValue('cnaePrincipal', lookupData.atividadePrincipal.codigo);
              setCnaePrimarySearchTerm(`${lookupData.atividadePrincipal.codigo} - ${lookupData.atividadePrincipal.descricao}`);
            }

            // CNAE Secondary (first one if exists)
            if (lookupData.atividadesSecundarias && lookupData.atividadesSecundarias.length > 0 && !formik.values.cnaeSecondary) {
              const secondary = lookupData.atividadesSecundarias[0];
              formik.setFieldValue('cnaeSecondary', secondary.codigo);
              setCnaeSecondarySearchTerm(`${secondary.codigo} - ${secondary.descricao}`);
            }

            // Contact info
            if (lookupData.email && !formik.values.email) {
              formik.setFieldValue('email', lookupData.email);
            }
            if (lookupData.telefone && !formik.values.phone) {
              formik.setFieldValue('phone', formatPhone(lookupData.telefone));
            }

            // Address - update each field individually
            if (lookupData.endereco) {
              const addr = lookupData.endereco;

              if (addr.logradouro) {
                formik.setFieldValue('headquartersAddress.street', addr.logradouro);
              }
              if (addr.numero) {
                formik.setFieldValue('headquartersAddress.number', addr.numero);
              }
              if (addr.complemento) {
                formik.setFieldValue('headquartersAddress.complement', addr.complemento);
              }
              if (addr.bairro) {
                formik.setFieldValue('headquartersAddress.neighborhood', addr.bairro);
              }
              if (addr.municipio) {
                formik.setFieldValue('headquartersAddress.city', addr.municipio);
              }
              if (addr.uf) {
                formik.setFieldValue('headquartersAddress.state', addr.uf);
              }
              if (addr.cep) {
                formik.setFieldValue('headquartersAddress.cep', formatCEP(addr.cep));
              }
            }

            // Phase 2 Fields from CNPJ Lookup (read-only)
            if (lookupData.porte) {
              formik.setFieldValue('porte', lookupData.porte);
            }
            if (lookupData.capitalSocial) {
              // Handle both Brazilian format (5.000,00) and American format (5000.00)
              let capital: number;
              const valueStr = String(lookupData.capitalSocial);

              // If contains comma, it's Brazilian format: replace . with nothing and , with .
              if (valueStr.includes(',')) {
                capital = parseFloat(valueStr.replace(/[^\d,]/g, '').replace(',', '.'));
              } else {
                // American format: just parse directly
                capital = parseFloat(valueStr.replace(/[^\d.]/g, ''));
              }

              if (!isNaN(capital)) {
                formik.setFieldValue('capitalSocial', capital);
              }
            }
            if (lookupData.dataAbertura) {
              // dataAbertura comes as Date or string, format as YYYY-MM-DD
              const date = new Date(lookupData.dataAbertura);
              if (!isNaN(date.getTime())) {
                const formatted = date.toISOString().split('T')[0];
                formik.setFieldValue('dataAbertura', formatted);
              }
            }
          }
        }
      } catch (error) {
        console.error('CNPJ validation error:', error);
      } finally {
        setValidatingCnpj(false);
      }
    }
  };

  // Phone Formatting
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    formik.setFieldValue('phone', formatted);
  };

  // CEP Lookup
  const handleCepChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCEP(e.target.value);
    formik.setFieldValue('headquartersAddress.cep', formatted);

    // Lookup address when CEP is complete
    if (cleanDigits(formatted).length === 8) {
      setLoadingCep(true);
      try {
        const address = await cepService.getAddressByCep(formatted);

        if (address) {
          // Update all address fields at once
          const currentValues = formik.values;
          formik.setValues({
            ...currentValues,
            headquartersAddress: {
              ...currentValues.headquartersAddress,
              street: address.street || '',
              neighborhood: address.neighborhood || '',
              city: address.city || '',
              state: address.state || '',
              cep: formatted,
            }
          });
        }
      } catch (error: any) {
        console.error('CEP lookup error:', error);
      } finally {
        setLoadingCep(false);
      }
    }
  };

  // CNAE Search for primary
  const handleCnaePrimarySearch = async (term: string) => {
    setCnaePrimarySearchTerm(term);
    if (term.length >= 3) {
      setLoadingCnaePrimary(true);
      try {
        const results = await cnaeService.search(term);
        setCnaePrimaryResults(results);
      } catch (error) {
        console.error('CNAE search error:', error);
        setCnaePrimaryResults([]);
      } finally {
        setLoadingCnaePrimary(false);
      }
    } else {
      setCnaePrimaryResults([]);
      setLoadingCnaePrimary(false);
    }
  };

  const handleCnaePrimarySelect = (cnae: any) => {
    formik.setFieldValue('cnaePrincipal', cnae.code);
    setCnaePrimarySearchTerm(`${cnae.code} - ${cnae.description}`);
    setCnaePrimaryResults([]);
  };

  // CNAE Search for secondary
  const handleCnaeSecondarySearch = async (term: string) => {
    setCnaeSecondarySearchTerm(term);
    if (term.length >= 3) {
      setLoadingCnaeSecondary(true);
      try {
        const results = await cnaeService.search(term);
        setCnaeSecondaryResults(results);
      } catch (error) {
        console.error('CNAE search error:', error);
        setCnaeSecondaryResults([]);
      } finally {
        setLoadingCnaeSecondary(false);
      }
    } else {
      setCnaeSecondaryResults([]);
      setLoadingCnaeSecondary(false);
    }
  };

  const handleCnaeSecondarySelect = (cnae: any) => {
    formik.setFieldValue('cnaeSecondary', cnae.code);
    setCnaeSecondarySearchTerm(`${cnae.code} - ${cnae.description}`);
    setCnaeSecondaryResults([]);
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert(t('Por favor, selecione apenas arquivos de imagem'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert(t('A imagem deve ter no máximo 5MB'));
      return;
    }

    // Create preview and store base64 for upload on form submit
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64Data = reader.result as string;
      setLogoPreview(base64Data);
      setLogoBase64(base64Data); // Store for upload on submit
    };
    reader.readAsDataURL(file);
  };

  return (
    <Form onSubmit={handleFormSubmit}>
      <Row>
        <Col lg={12}>
          <Alert color="info" className="mb-4">
            <i className="ri-information-line me-2"></i>
            {t(
              'Fase 2: Preencha os dados básicos e complementares da empresa. Os campos obrigatórios estão marcados com *.'
            )}
          </Alert>

          {/* Show validation errors if form was submitted but has errors */}
          {formik.submitCount > 0 && Object.keys(formik.errors).length > 0 && (
            <Alert color="danger" className="mb-4">
              <h6 className="alert-heading">
                <i className="ri-error-warning-line me-2"></i>
                {t('Erros de Validação')}
              </h6>
              <ul className="mb-0">
                {Object.entries(formik.errors).map(([key, value]) => (
                  <li key={key}>
                    <strong>{t(key)}:</strong> {String(value)}
                  </li>
                ))}
              </ul>
            </Alert>
          )}
        </Col>
      </Row>

      {/* Company Basic Info */}
      <Card className="mb-4">
        <CardHeader>
          <h5 className="card-title mb-0">
            <i className="ri-building-line me-2"></i>
            {t('Dados Básicos da Empresa')}
          </h5>
        </CardHeader>
        <CardBody>
          {/* Logo and Basic Info Row */}
          <Row className="mb-4">
            {/* Logo on the left */}
            <Col md={3} className="d-flex flex-column align-items-center">
              <div className="profile-user position-relative d-inline-block mb-3">
                {(logoPreview || formik.values.logoUrl) ? (
                  <img
                    src={logoPreview || formik.values.logoUrl}
                    alt="Company Logo"
                    className="rounded img-thumbnail"
                    style={{
                      width: '220px',
                      height: '220px',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <div
                    className="img-thumbnail rounded d-flex align-items-center justify-content-center bg-light"
                    style={{
                      width: '220px',
                      height: '220px',
                    }}
                  >
                    <i className="ri-building-line" style={{ fontSize: '5rem', color: '#adb5bd' }}></i>
                  </div>
                )}
                <div className="avatar-xs p-0 rounded-circle profile-photo-edit">
                  <Input
                    innerRef={fileInputRef}
                    id="company-logo-file-input"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="profile-img-file-input"
                  />
                  <Label
                    htmlFor="company-logo-file-input"
                    className="profile-photo-edit avatar-xs"
                  >
                    <span className="avatar-title rounded-circle bg-light text-body">
                      <i className="ri-camera-fill"></i>
                    </span>
                  </Label>
                </div>
              </div>
              <div className="text-muted text-center">
                <small>{t('Formatos aceitos: JPG, PNG, GIF. Tamanho máximo: 5MB')}</small>
              </div>
            </Col>

            {/* Fields on the right */}
            <Col md={9}>
              <Row>
                {/* Company ID - Auto-generated, Read-only */}
                <Col md={12}>
                  <FormGroup>
                    <Label for="companyNumber">
                      {t('Id')}
                    </Label>
                    <Input
                      id="companyNumber"
                      name="companyNumber"
                      value={
                        formik.values.companyNumber
                          ? String(formik.values.companyNumber).padStart(4, '0')
                          : t('Pendente')
                      }
                      readOnly
                      disabled
                      className="bg-light"
                    />
                    {!formik.values.companyNumber && (
                      <small className="text-muted">
                        {t('O ID será gerado automaticamente ao salvar')}
                      </small>
                    )}
                  </FormGroup>
                </Col>

                <Col md={12}>
                  <FormGroup>
                    <Label for="name">
                      {t('Razão Social')} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={!!(formik.touched.name && formik.errors.name)}
                    />
                    <FormFeedback>{formik.errors.name as string}</FormFeedback>
                  </FormGroup>
                </Col>

                <Col md={12}>
                  <FormGroup>
                    <Label for="cnpj">
                      {t('CNPJ')} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="cnpj"
                      name="cnpj"
                      value={formik.values.cnpj}
                      onChange={handleCnpjChange}
                      onBlur={formik.handleBlur}
                      invalid={!!(formik.touched.cnpj && formik.errors.cnpj)}
                      placeholder="00.000.000/0000-00"
                    />
                    {validatingCnpj && (
                      <small className="text-muted">
                        <i className="ri-loader-4-line rotating me-1"></i>
                        {t('Validando CNPJ...')}
                      </small>
                    )}
                    <FormFeedback>{formik.errors.cnpj as string}</FormFeedback>
                  </FormGroup>
                </Col>
              </Row>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="taxRegime">
                  {t('Regime Tributário')} <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  id="taxRegime"
                  name="taxRegime"
                  value={formik.values.taxRegime || ''}
                  onChange={(e) => {
                    const value = e.target.value ? parseInt(e.target.value, 10) : undefined;
                    formik.setFieldValue('taxRegime', value);
                  }}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.taxRegime && formik.errors.taxRegime)}
                >
                  <option value="">{t('Selecione...')}</option>
                  {TAX_REGIMES.map((regime) => (
                    <option key={regime.value} value={regime.value}>
                      {regime.label}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{formik.errors.taxRegime as string}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label for="companyType">
                  {t('Tipo Empresarial')}
                </Label>
                <Input
                  type="select"
                  id="companyType"
                  name="companyType"
                  value={formik.values.companyType || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.companyType && formik.errors.companyType)}
                >
                  <option value="">{t('Selecione...')}</option>
                  {COMPANY_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{formik.errors.companyType as string}</FormFeedback>
              </FormGroup>
            </Col>

            {/* Conditional Código do Simples - only show when Simples Nacional is selected */}
            {(String(formik.values.taxRegime) === '1') && (
              <Col md={6}>
                <FormGroup>
                  <Label for="simplesCode">{t('Código do Simples')}</Label>
                  <Input
                    id="simplesCode"
                    name="simplesCode"
                    value={formik.values.simplesCode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder={t('Código de acesso ao Simples Nacional')}
                    maxLength={20}
                  />
                </FormGroup>
              </Col>
            )}

            {/* Anexo do Simples Nacional - only show when Simples Nacional is selected */}
            {String(formik.values.taxRegime) === '1' && (
              <Col md={6}>
                <FormGroup>
                  <Label for="anexo">
                    {t('Anexo do Simples Nacional')} <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    id="anexo"
                    name="anexo"
                    value={formik.values.anexo || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={!!(formik.touched.anexo && formik.errors.anexo)}
                  >
                    <option value="">{t('Selecione...')}</option>
                    <option value="I">Anexo I - Comércio</option>
                    <option value="II">Anexo II - Indústria</option>
                    <option value="III">Anexo III - Serviços</option>
                    <option value="IV">Anexo IV - Serviços</option>
                    <option value="V">Anexo V - Serviços</option>
                  </Input>
                  <FormFeedback>{formik.errors.anexo as string}</FormFeedback>
                  <small className="form-text text-muted">
                    {t('Anexo da tabela do Simples Nacional')}
                  </small>
                </FormGroup>
              </Col>
            )}

            {/* Segmento - Always visible */}
            <Col md={6}>
              <FormGroup>
                <Label for="segmento">
                  {t('Segmento')} <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  id="segmento"
                  name="segmento"
                  value={formik.values.segmento || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.segmento && formik.errors.segmento)}
                >
                  <option value="">{t('Selecione...')}</option>
                  <option value="Comercio">Comércio</option>
                  <option value="Industria">Indústria</option>
                  <option value="Servicos">Serviços</option>
                </Input>
                {formik.touched.segmento && formik.errors.segmento ? (
                  <small className="text-danger d-block mt-1">
                    {t('Segmento é obrigatório')}
                  </small>
                ) : (
                  <small className="form-text text-muted">
                    {t('Segmento de atuação da empresa')}
                  </small>
                )}
              </FormGroup>
            </Col>

            {/* Ramo de Atividade - Always required */}
            <Col md={6}>
              <FormGroup>
                <Label for="ramoAtividadeId">
                  {t('Ramo de Atividade')} <span className="text-danger">*</span>
                </Label>
                <Select<{ value: string; label: string }>
                  id="ramoAtividadeId"
                  name="ramoAtividadeId"
                  options={ramosAtividade.map((ramo) => ({
                    value: ramo.id,
                    label: ramo.nome,
                  }))}
                  value={
                    formik.values.ramoAtividadeId
                      ? {
                          value: formik.values.ramoAtividadeId,
                          label: ramosAtividade.find((r) => r.id === formik.values.ramoAtividadeId)?.nome || '',
                        }
                      : null
                  }
                  onChange={(selectedOption: { value: string; label: string } | null) => {
                    formik.setFieldValue('ramoAtividadeId', selectedOption?.value || '');
                  }}
                  onBlur={() => formik.setFieldTouched('ramoAtividadeId', true)}
                  isLoading={loadingRamosAtividade}
                  isDisabled={loadingRamosAtividade}
                  isClearable
                  placeholder={loadingRamosAtividade ? t('Carregando...') : t('Digite para buscar ou selecione...')}
                  noOptionsMessage={() => t('Nenhuma opção encontrada')}
                  loadingMessage={() => t('Carregando...')}
                  className={formik.touched.ramoAtividadeId && formik.errors.ramoAtividadeId ? 'is-invalid' : ''}
                  styles={{
                    control: (base: any, state: any) => ({
                      ...base,
                      borderColor: formik.touched.ramoAtividadeId && formik.errors.ramoAtividadeId
                        ? '#dc3545'
                        : state.isFocused
                        ? '#86b7fe'
                        : '#ced4da',
                      boxShadow: state.isFocused
                        ? formik.touched.ramoAtividadeId && formik.errors.ramoAtividadeId
                          ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
                          : '0 0 0 0.25rem rgba(13, 110, 253, 0.25)'
                        : 'none',
                      '&:hover': {
                        borderColor: formik.touched.ramoAtividadeId && formik.errors.ramoAtividadeId
                          ? '#dc3545'
                          : state.isFocused
                          ? '#86b7fe'
                          : '#ced4da',
                      },
                    }),
                  }}
                />
                {formik.touched.ramoAtividadeId && formik.errors.ramoAtividadeId && (
                  <div className="invalid-feedback d-block">
                    {formik.errors.ramoAtividadeId as string}
                  </div>
                )}
                <small className="form-text text-muted">
                  {t('Área de atuação específica da empresa')}
                </small>
              </FormGroup>
            </Col>

            {/* Porte (from Receita Federal) */}
            <Col md={2}>
              <FormGroup>
                <Label for="porte">{t('Porte')}</Label>
                <Input
                  id="porte"
                  name="porte"
                  value={formik.values.porte}
                  readOnly
                  disabled
                  className="bg-light"
                  placeholder={t('Preenchido automaticamente')}
                />
                <small className="form-text text-muted">
                  {t('Informação obtida da Receita Federal')}
                </small>
              </FormGroup>
            </Col>

            {/* Capital Social (from Receita Federal) */}
            <Col md={2}>
              <FormGroup>
                <Label for="capitalSocial">{t('Capital Social')}</Label>
                <Input
                  type="text"
                  id="capitalSocial"
                  name="capitalSocial"
                  value={
                    formik.values.capitalSocial
                      ? `R$ ${parseFloat(formik.values.capitalSocial).toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                      : ''
                  }
                  readOnly
                  disabled
                  className="bg-light"
                  placeholder={t('Preenchido automaticamente')}
                />
                <small className="form-text text-muted">
                  {t('Informação obtida da Receita Federal')}
                </small>
              </FormGroup>
            </Col>

            {/* Data de Abertura (from Receita Federal) */}
            <Col md={2}>
              <FormGroup>
                <Label for="dataAbertura">{t('Data de Abertura')}</Label>
                <Input
                  type="date"
                  id="dataAbertura"
                  name="dataAbertura"
                  value={formik.values.dataAbertura || ''}
                  readOnly
                  disabled
                  className="bg-light"
                />
                <small className="form-text text-muted">
                  {t('Informação obtida da Receita Federal')}
                </small>
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Tax and Registration Info */}
      <Card className="mb-4">
        <CardHeader>
          <h5 className="card-title mb-0">
            <i className="ri-file-list-3-line me-2"></i>
            {t('Informações Fiscais e de Registro')}
          </h5>
        </CardHeader>
        <CardBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="cnaePrincipal">
                  {t('CNAE Principal')} <span className="text-danger">*</span>
                </Label>
                <Input
                  id="cnaePrincipal"
                  name="cnaePrincipal"
                  value={cnaePrimarySearchTerm || formik.values.cnaePrincipal}
                  onChange={(e) => handleCnaePrimarySearch(e.target.value)}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    // If user typed a value but didn't select from dropdown, use what they typed
                    if (e.target.value && !formik.values.cnaePrincipal) {
                      formik.setFieldValue('cnaePrincipal', e.target.value);
                    }
                  }}
                  invalid={
                    !!(formik.touched.cnaePrincipal && formik.errors.cnaePrincipal)
                  }
                  placeholder={t('Digite para buscar CNAE')}
                  autoComplete="off"
                />
                {loadingCnaePrimary && (
                  <small className="text-muted">
                    <i className="ri-loader-4-line rotating me-1"></i>
                    {t('Buscando CNAE...')}
                  </small>
                )}
                {cnaePrimaryResults.length > 0 && (
                  <div className="list-group position-absolute w-100" style={{ zIndex: 1000 }}>
                    {cnaePrimaryResults.slice(0, 5).map((cnae) => (
                      <button
                        key={cnae.code}
                        type="button"
                        className="list-group-item list-group-item-action"
                        onClick={() => handleCnaePrimarySelect(cnae)}
                      >
                        <strong>{cnae.code}</strong> - {cnae.description}
                      </button>
                    ))}
                  </div>
                )}
                <FormFeedback>{formik.errors.cnaePrincipal as string}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label for="cnaeSecondary">{t('CNAE Secundário')}</Label>
                <Input
                  id="cnaeSecondary"
                  value={cnaeSecondarySearchTerm || formik.values.cnaeSecondary}
                  onChange={(e) => handleCnaeSecondarySearch(e.target.value)}
                  onBlur={formik.handleBlur}
                  placeholder={t('Digite para buscar CNAE')}
                  autoComplete="off"
                />
                {loadingCnaeSecondary && (
                  <small className="text-muted">
                    <i className="ri-loader-4-line rotating me-1"></i>
                    {t('Buscando CNAE...')}
                  </small>
                )}
                {cnaeSecondaryResults.length > 0 && (
                  <div className="list-group position-absolute w-100" style={{ zIndex: 1000 }}>
                    {cnaeSecondaryResults.slice(0, 5).map((cnae) => (
                      <button
                        key={cnae.code}
                        type="button"
                        className="list-group-item list-group-item-action"
                        onClick={() => handleCnaeSecondarySelect(cnae)}
                      >
                        <strong>{cnae.code}</strong> - {cnae.description}
                      </button>
                    ))}
                  </div>
                )}
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label for="nire">{t('NIRE')}</Label>
                <Input
                  id="nire"
                  name="nire"
                  value={formik.values.nire}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={t('Número de Identificação do Registro de Empresa')}
                />
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label for="professionalEntity">{t('Entidade de Classe')}</Label>
                <Input
                  id="professionalEntity"
                  name="professionalEntity"
                  value={formik.values.professionalEntity}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={t('Ex: CRC, CRM, OAB, etc.')}
                />
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label for="stateRegistration">{t('Inscrição Estadual')}</Label>
                <Input
                  id="stateRegistration"
                  name="stateRegistration"
                  value={formik.values.stateRegistration}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={t('Digite o número ou "Isento"')}
                />
                <small className="form-text text-muted">
                  {t('Informe o número da inscrição ou digite "Isento"')}
                </small>
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label for="municipalRegistration">{t('Inscrição Municipal')}</Label>
                <Input
                  id="municipalRegistration"
                  name="municipalRegistration"
                  value={formik.values.municipalRegistration}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={t('Número da Inscrição Municipal')}
                />
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label for="invoicePassword">{t('Senha de Nota Fiscal')}</Label>
                <div className="position-relative">
                  <Input
                    type={showInvoicePassword ? 'text' : 'password'}
                    id="invoicePassword"
                    name="invoicePassword"
                    value={formik.values.invoicePassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder={t('Senha para emissão de notas fiscais')}
                    className="pe-5"
                  />
                  <button
                    type="button"
                    className="btn btn-link position-absolute end-0 top-0 text-muted"
                    onClick={() => setShowInvoicePassword(!showInvoicePassword)}
                    style={{ textDecoration: 'none' }}
                  >
                    <i className={showInvoicePassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  </button>
                </div>
                <small className="form-text text-muted">
                  {t('Será armazenada de forma criptografada')}
                </small>
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label for="invoiceEmissionPortalUrl">
                  {t('Link do Portal de Emissão de NF')}
                </Label>
                <Input
                  type="url"
                  id="invoiceEmissionPortalUrl"
                  name="invoiceEmissionPortalUrl"
                  value={formik.values.invoiceEmissionPortalUrl}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.invoiceEmissionPortalUrl && formik.errors.invoiceEmissionPortalUrl)}
                  placeholder="https://exemplo.com/portal-nfe"
                />
                <FormFeedback>{formik.errors.invoiceEmissionPortalUrl as string}</FormFeedback>
                <small className="form-text text-muted">
                  {t('URL do portal de acesso à emissão de nota fiscal (opcional)')}
                </small>
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Contact Information */}
      <Card className="mb-4">
        <CardHeader>
          <h5 className="card-title mb-0">
            <i className="ri-phone-line me-2"></i>
            {t('Informações de Contato')}
          </h5>
        </CardHeader>
        <CardBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="email">{t('Email da Empresa')}</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.email && formik.errors.email)}
                  placeholder="contato@empresa.com.br"
                />
                <FormFeedback>{formik.errors.email as string}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label for="phone">{t('Telefone da Empresa')}</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formik.values.phone}
                  onChange={handlePhoneChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.phone && formik.errors.phone)}
                  placeholder="(00) 00000-0000"
                />
                <FormFeedback>{formik.errors.phone as string}</FormFeedback>
              </FormGroup>
            </Col>

            {/* Address Section */}
            <Col xs={12}>
              <hr className="my-3" />
              <h6 className="text-muted mb-3">
                <i className="ri-map-pin-line me-2"></i>
                {t('Endereço da Sede')}
              </h6>
            </Col>

            <Col md={3}>
              <FormGroup>
                <Label for="headquartersAddress.cep">{t('CEP')}</Label>
                <Input
                  id="headquartersAddress.cep"
                  name="headquartersAddress.cep"
                  value={formik.values.headquartersAddress?.cep || ''}
                  onChange={handleCepChange}
                  onBlur={formik.handleBlur}
                  placeholder="00000-000"
                />
                {loadingCep && (
                  <small className="text-muted">
                    <i className="ri-loader-4-line rotating me-1"></i>
                    {t('Buscando endereço...')}
                  </small>
                )}
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label for="headquartersAddress.street">{t('Rua')}</Label>
                <Input
                  id="headquartersAddress.street"
                  name="headquartersAddress.street"
                  value={formik.values.headquartersAddress?.street || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </FormGroup>
            </Col>

            <Col md={3}>
              <FormGroup>
                <Label for="headquartersAddress.number">
                  {t('Número')} {formik.values.headquartersAddress?.cep && <span className="text-danger">*</span>}
                </Label>
                <Input
                  id="headquartersAddress.number"
                  name="headquartersAddress.number"
                  value={formik.values.headquartersAddress?.number || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!((formik.touched.headquartersAddress as any)?.number && (formik.errors.headquartersAddress as any)?.number)}
                />
                <FormFeedback>{(formik.errors.headquartersAddress as any)?.number}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label for="headquartersAddress.complement">{t('Complemento')}</Label>
                <Input
                  id="headquartersAddress.complement"
                  name="headquartersAddress.complement"
                  value={formik.values.headquartersAddress?.complement || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label for="headquartersAddress.neighborhood">{t('Bairro')}</Label>
                <Input
                  id="headquartersAddress.neighborhood"
                  name="headquartersAddress.neighborhood"
                  value={formik.values.headquartersAddress?.neighborhood || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </FormGroup>
            </Col>

            <Col md={3}>
              <FormGroup>
                <Label for="headquartersAddress.city">{t('Cidade')}</Label>
                <Input
                  id="headquartersAddress.city"
                  name="headquartersAddress.city"
                  value={formik.values.headquartersAddress?.city || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </FormGroup>
            </Col>

            <Col md={1}>
              <FormGroup>
                <Label for="headquartersAddress.state">{t('UF')}</Label>
                <Input
                  type="select"
                  id="headquartersAddress.state"
                  name="headquartersAddress.state"
                  value={formik.values.headquartersAddress?.state || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value="">-</option>
                  {BRAZILIAN_STATES.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Additional Info */}
      <Card className="mb-4">
        <CardHeader>
          <h5 className="card-title mb-0">
            <i className="ri-information-line me-2"></i>
            {t('Informações Adicionais')}
          </h5>
        </CardHeader>
        <CardBody>
          <Row>
            <Col xs={12}>
              <FormGroup>
                <Label for="observacao">{t('Observações')}</Label>
                <Input
                  type="textarea"
                  id="observacao"
                  name="observacao"
                  value={formik.values.observacao}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  rows={3}
                  maxLength={500}
                  placeholder={t('Informações adicionais sobre a empresa (máximo 500 caracteres)')}
                />
                <small className="form-text text-muted">
                  {formik.values.observacao?.length || 0}/500 {t('caracteres')}
                </small>
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Form Actions */}
      <div className="d-flex justify-content-between gap-2">
        <Button color="secondary" outline onClick={onPrevious}>
          <i className="ri-arrow-left-line me-1"></i>
          {t('Anterior')}
        </Button>

        <Button type="submit" color="primary" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <i className="ri-loader-4-line rotating me-1"></i>
              {isFinalPhase ? t('Finalizando...') : t('Salvando...')}
            </>
          ) : (
            <>
              {isFinalPhase ? (
                <>
                  <i className="ri-check-line me-1"></i>
                  {t('Finalizar Cadastro')}
                </>
              ) : (
                <>
                  {t('Próximo')}
                  <i className="ri-arrow-right-line ms-1"></i>
                </>
              )}
            </>
          )}
        </Button>
      </div>
    </Form>
  );
};

export default Phase2CompanyForm;
